/**
 * Shared compact layout bits for the three portfolio charts (Expected
 * Returns, Volatilities, Allocations).
 *
 * At ~380px-wide cells the repo-wide 18px title / 16px legend dwarf the
 * fan grid sitting right above, and the shared fixed monthly `dtick`
 * crowds the x-axis into rotated labels. This module pins one font scale
 * (title 15 / legend 12) and auto-spaced horizontal date ticks so the
 * three charts read as one family.
 */
import type { Layout } from 'plotly.js';
import { PLOTLY_FONT_FAMILY } from '@/lib/plotly/defaults';
import { chartLayout } from '@/lib/plotly/layoutPresets';

export interface CompactChartOptions {
  title: string;
  /** Extra margin tweaks; merged over the compact default (t: 70). */
  marginOverride?: Partial<Layout['margin']>;
}

/** `chartLayout` preset with title/legend scaled down for compact cells. */
export function compactChartLayout(opts: CompactChartOptions): Partial<Layout> {
  const preset = chartLayout({
    title: opts.title,
    marginOverride: { t: 70, ...(opts.marginOverride ?? {}) },
  });
  return {
    ...preset,
    title: {
      ...(preset.title as object),
      font: { size: 15, family: PLOTLY_FONT_FAMILY, color: '#000000' },
    },
    legend: {
      ...(preset.legend as object),
      font: { size: 12, family: PLOTLY_FONT_FAMILY, color: '#000000' },
    },
  };
}

/**
 * Auto-spaced horizontal date ticks for a narrow chart: cap the count and
 * keep labels level instead of forcing a fixed `dtick` that rotates them.
 */
export const COMPACT_DATE_TICKS = { nticks: 5, tickangle: 0 } as const;
